import isEqual from 'lodash/isEqual'
import { useCallback } from 'react'
import { useFilterFieldsContext } from '../../../context'
import type { FieldValues, GroupedFieldValues } from '../../../types'
import removeGroupFromComposition from '../removeGroupFromComposition'

export type UseSectionGroupActionsParams = {
  groupPosition: number
  groupsLength: number
  filteractionValue: string
  setFilteractionValue: (value: string) => void
  setChangedKeysData: (value: GroupedFieldValues | undefined) => void
  setGroupPosition: (position: number | ((prev: number) => number)) => void
}

export type UseSectionGroupActionsResult = {
  handleChangeFieldValues: (value: FieldValues) => void
  handleClearCurrentGroup: () => void
  handleClearAll: () => void
  handleRemoveCurrentGroup: () => void
  isEmpty: boolean
}

export const useSectionGroupActions = ({
  groupPosition,
  groupsLength,
  filteractionValue,
  setFilteractionValue,
  setChangedKeysData,
  setGroupPosition
}: UseSectionGroupActionsParams): UseSectionGroupActionsResult => {
  const {
    form: {
      initialGroupedFieldValues,
      composition,
      groupedFieldValues,
      handleChange,
      handleComposition,
      handleClear
    }
  } = useFilterFieldsContext()

  const initialFieldValues = initialGroupedFieldValues[1]
  const fieldValues = groupedFieldValues[groupPosition]
  const handleChangeFieldValues = useCallback(
    (value: FieldValues) => {
      handleChange({ [groupPosition]: value })
    },
    [groupPosition, handleChange]
  )

  const handleClearCurrentGroup = useCallback(
    () => handleChangeFieldValues(initialFieldValues),
    [handleChangeFieldValues, initialFieldValues]
  )

  const handleClearAll = useCallback(() => {
    handleClear?.()
    setFilteractionValue('')
    setChangedKeysData(undefined)
  }, [handleClear, setFilteractionValue, setChangedKeysData])

  const handleRemoveCurrentGroup = useCallback(() => {
    handleComposition(removeGroupFromComposition(groupPosition, composition))

    if (groupsLength === groupPosition) {
      setGroupPosition((currentGroup) => currentGroup - 1)
    }

    handleChange({
      [groupPosition]: undefined
    })
  }, [
    groupPosition,
    groupsLength,
    composition,
    handleComposition,
    handleChange,
    setGroupPosition
  ])

  const isEmpty =
    groupsLength === 1 &&
    filteractionValue === '' &&
    isEqual(fieldValues, initialFieldValues)

  return {
    handleChangeFieldValues,
    handleClearCurrentGroup,
    handleClearAll,
    handleRemoveCurrentGroup,
    isEmpty
  }
}
